import dayjs from 'dayjs';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { InformationBoxTheme, ArtworkSummaryTheme, IconName, Language, ButtonTheme } from '../../enums';
import { useFormatDate, useParsers } from '../../hooks';
import { UseFormatDate, UseParsers } from '../../interfaces';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';
import { InformationBox } from '../informationBox/informationBox.component';

import {
  Attribute,
  BottomContainer,
  Container,
  CountDown,
  CountDownLabel,
  InfoButton,
  Label,
  MarkdownAttribute,
  Price,
  PriceContainer,
  PricePrefix,
  SalesFinished,
  StyledButton,
  StyledInfoText,
  StyledInput,
  TimerContainer,
  Title
} from './artworkSummary.styled';
import { ArtworkSummaryProps } from './artworkSummary.types';

export const ArtworkSummary: React.FC<ArtworkSummaryProps> = (props: ArtworkSummaryProps): JSX.Element => {
  const {
    artworkSummaryTheme,
    informationBoxDetails,
    artworkAttributes,
    title,
    price,
    artworkSummaryAuctionDetails,
    language = Language.Pl,
    currency = 'zł',
    onButtonClick,
    onSecondaryButtonClick,
    disabled = false,
    inputDefaultValue,
    isAuctionEnded = false,
    isTitleHeading,
    className
  }: ArtworkSummaryProps = props;
  const { t }: TransProps<never> = useTranslation();
  const [countDownDate, setCountDownDate]: UseState<string> = useState<string>('');
  const [inputValue, setInputValue]: UseState<string> = useState<string>(inputDefaultValue ?? '');
  const [countDownLabel, setCountDownLabel]: UseState<string> = useState<string>('');
  const { formatDate, isReady }: UseFormatDate = useFormatDate(language);
  const { parsePrice }: UseParsers = useParsers();
  const auctionStart: dayjs.Dayjs = dayjs(formatDate(artworkSummaryAuctionDetails?.auctionStart ?? 0, 'YYYY-MM-DD HH:mm:ss'));
  const auctionDeadline: dayjs.Dayjs = dayjs(formatDate(artworkSummaryAuctionDetails?.auctionDeadline ?? 0, 'YYYY-MM-DD HH:mm:ss'));
  const todaysDate: dayjs.Dayjs = dayjs();

  const calculateDifference: (time: dayjs.Dayjs) => void = (time: dayjs.Dayjs): void => {
    const differenceInDays: number = time.diff(todaysDate, 'day');
    const differenceInHours: number = Math.floor(time.diff(todaysDate, 'hour') % 24);
    const differenceInMinutes: number = Math.floor(time.diff(todaysDate, 'minute') % 60);
    const differenceInSeconds: number = Math.floor(time.diff(todaysDate, 'second') % 60);
    if (time.unix() >= todaysDate.unix()) {
      setCountDownDate(`
      ${differenceInDays < 10 ? `0${differenceInDays}` : differenceInDays}d
      ${differenceInHours < 10 ? `0${differenceInHours}` : differenceInHours}h
      ${differenceInMinutes < 10 ? `0${differenceInMinutes}` : differenceInMinutes}m
      ${differenceInSeconds < 10 ? `0${differenceInSeconds}` : differenceInSeconds}s
    `);
    }
  };

  const countTime: dayjs.Dayjs | null = useMemo(
    (): dayjs.Dayjs | null => {
      if (artworkSummaryAuctionDetails && isReady) {
        if (!!auctionStart && auctionStart.unix() > todaysDate.unix()) {
          return auctionStart;
        } else if (!!auctionDeadline && auctionDeadline.unix() >= todaysDate.unix()) {
          return auctionDeadline;
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    [isReady, artworkSummaryAuctionDetails, countDownDate, countDownLabel]
  );
 
  useEffect(
    (): (() => void) | undefined => {
      if (artworkSummaryAuctionDetails && isReady) {
        const intervalId: NodeJS.Timer = setInterval((): void => {
          if (countTime && !!auctionStart && auctionStart.unix() > todaysDate.unix() && artworkSummaryAuctionDetails.auctionStart) {
            calculateDifference(countTime);
            setCountDownLabel(t(
              'onarte.ui.artworkSummary.auctionStart', 
              { date: formatDate(artworkSummaryAuctionDetails?.auctionStart, 'DD MMMM, HH:mm:ss') }
            ));
          } else if (countTime && auctionDeadline && auctionDeadline.unix() > todaysDate.unix()) {
            calculateDifference(countTime);
            setCountDownLabel(t(
              'onarte.ui.artworkSummary.auctionDeadline', 
              { date: formatDate(artworkSummaryAuctionDetails?.auctionDeadline, 'DD MMMM, HH:mm:ss') }
            ));
          } else {
            setCountDownDate(t('onarte.ui.artworkSummary.auctionEnded'));
            setCountDownLabel(t(
              'onarte.ui.artworkSummary.auctionDeadline', 
              { date: formatDate(artworkSummaryAuctionDetails?.auctionDeadline, 'DD MMMM, HH:mm:ss') }
            ));
          }
        }, 1000);
    
        return((): void => clearInterval(intervalId));
      }
    }, [isReady, countTime]);

  useEffect(
    (): void => {
      if (!inputDefaultValue) {
        return;
      }

      setInputValue(inputDefaultValue);
    },
    [inputDefaultValue]
  );

  return (
    <Container className={className}>
      <InformationBox informationDetails={informationBoxDetails} theme={InformationBoxTheme.Clean} />
      <Title as={isTitleHeading ? 'h1' : undefined}>{title}</Title>
      {artworkAttributes.map((attribute: string): JSX.Element => (
        <Attribute key={attribute}>{attribute}</Attribute>
      ))}
      {!!price && (
        <PriceContainer>
          {artworkSummaryTheme === ArtworkSummaryTheme.Auction && (
            <PricePrefix>{t('onarte.ui.artworkSummary.pricePrefix')}</PricePrefix>
          )}
          <Price>{`${parsePrice(price)} ${currency}`}</Price>
        </PriceContainer>
      )}
      {artworkSummaryTheme === ArtworkSummaryTheme.Auction && !!artworkSummaryAuctionDetails && (
        <>
          {artworkSummaryAuctionDetails.minimalPriceNotReached && (
            <MarkdownAttribute text={t('onarte.ui.artworkSummary.minimalPriceNotReached')} />
          )}
          <Attribute data-testid='artwork-auction-attributes'>
            {`${t('onarte.ui.artworkSummary.estimate')}
            ${parsePrice(artworkSummaryAuctionDetails.estimationMin)}
            - 
            ${parsePrice(artworkSummaryAuctionDetails.estimationMax)} 
            ${currency}`}
          </Attribute>
          <Attribute>{`${t('onarte.ui.artworkSummary.bidders')} ${artworkSummaryAuctionDetails.bidders}`}</Attribute>
          <BottomContainer data-testid='bottom-container'>
            <Label>
              {t('onarte.ui.artworkSummary.inputLabel')}
              <InfoButton>
                <Icon name={IconName.InfoIcon} size={20} />
                <StyledInfoText>{t('onarte.ui.artworkSummary.infoText')}</StyledInfoText>
              </InfoButton>
            </Label>
            <StyledInput
              icon={IconName.Zl}
              onChange={setInputValue}
              disabled={disabled || isAuctionEnded}
              value={(!isAuctionEnded && !disabled) ? inputValue : undefined} 
              withPriceParsing
              withOnlyNumbersAllowed
            />
            <StyledButton 
              label={t('onarte.ui.artworkSummary.buttonsLabel.bid')} 
              onClick={(): void => onButtonClick(inputValue)} 
              disabled={disabled || isAuctionEnded}
              id='artwork-summary-bidding-button'
            />
            {!!countDownDate && !!countDownLabel && (
              <TimerContainer>
                <CountDown>
                  {countDownDate}
                </CountDown>
                <CountDownLabel>
                  {countDownLabel}
                </CountDownLabel>
              </TimerContainer>
            )}
          </BottomContainer>
        </>
      )}
      {artworkSummaryTheme !== ArtworkSummaryTheme.Auction && (
        <BottomContainer $withBottomPadding={!isAuctionEnded} data-testid='container'>
          <StyledButton
            onClick={onButtonClick}
            label={
              artworkSummaryTheme === ArtworkSummaryTheme.BuyNow
                ? t('onarte.ui.artworkSummary.buttonsLabel.buyNow')
                : t('onarte.ui.artworkSummary.buttonsLabel.makeOffer')
            }
            disabled={disabled || isAuctionEnded}
            id={
              artworkSummaryTheme === ArtworkSummaryTheme.BuyNow
                ? 'artwork-summary-buy-now-button'
                : 'artwork-summary-make-offer-button'
            }
          />
          {artworkSummaryTheme === ArtworkSummaryTheme.BuyNow && (
            <StyledButton
              onClick={onSecondaryButtonClick}
              label={t('onarte.ui.artworkSummary.buttonsLabel.addToCart')}
              buttonTheme={ButtonTheme.SecondarySolidBig}
              disabled={disabled || isAuctionEnded}
              id='artwork-summary-add-to-cart-button'
            />
          )}
          {isAuctionEnded && (
            <SalesFinished>
              {t('onarte.ui.artworkSummary.salesFinished')}
            </SalesFinished>
          )}
        </BottomContainer>
      )}
    </Container>
  );
};
